"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";

import { useSignInVisibility } from "@/components/auth/sign-in-visibility";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import type { User } from "@/lib/auth/server";

function loginHref(pathname: string, search: string) {
  // Same default as proxy.ts — bare `/` needs no returnTo.
  if (pathname === "/" && !search) {
    return "/login";
  }

  const params = new URLSearchParams({ returnTo: pathname + search });
  return `/login?${params.toString()}`;
}

export function UserButton({ user }: { user: User | null }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const { hideSignIn } = useSignInVisibility();

  if (!user) {
    if (hideSignIn) {
      return null;
    }

    return (
      <Link href={loginHref(pathname, search ? `?${search}` : "")}>
        <Button variant="outline">Sign In</Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative size-8 rounded-full">
          <Avatar className="size-8">
            <AvatarImage src={user.avatarUrl ?? ""} alt={user.name ?? ""} />
            <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-muted-foreground text-xs leading-none">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {["admin", "active_client"].includes(user.accessLevel) && (
          <Link href="/folders">
            <DropdownMenuItem className="cursor-pointer">
              Documents
            </DropdownMenuItem>
          </Link>
        )}
        <Link href="/settings">
          <DropdownMenuItem className="cursor-pointer">
            Settings
          </DropdownMenuItem>
        </Link>
        <Link href="/logout" prefetch={false}>
          <DropdownMenuItem className="cursor-pointer">
            Sign Out
          </DropdownMenuItem>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
